import { matchPath, useLocation } from "react-router-dom";

import {
  CMP_PERMISSION_VIEW_ORDERS,
  CMP_PERMISSION_VIEW_RESULTS,
  CMP_PERMISSION_VIEW_SHOP
} from "@/components/organisations/constants";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  CMP_DASHBOARD_URL,
  CMP_LEARNING_HUB_COURSES_URL,
  CMP_LEARNING_HUB_DASHBOARD_URL,
  CMP_LEARNING_HUB_EVENTS_URL,
  CMP_LEARNING_HUB_URL,
  CMP_LEARNING_HUB_VIDEO_GUIDES_URL,
  CMP_LEARNING_HUB_WEBINARS_URL,
  CMP_ORDER_URL,
  CMP_ORDERS_URL,
  CMP_PATIENTS_CALLBACK_REQUESTS_URL,
  CMP_PATIENTS_MANAGE_PATIENTS_URL,
  CMP_PATIENTS_NEW_RESULTS_URL,
  CMP_PATIENTS_URL,
  CMP_SHOP_TAB_URL,
  CMP_SHOP_URL,
  CMP_STOCK_URL,
  CMP_SUPPORT_URL,
  CMP_CHECKOUT_URL
} from "@/core/urls";
import useCmpRelatedUrl from "@/hooks/organisations/useCmpRelatedUrl";
import useUserProfile from "@/hooks/useUserProfile";
import Group from "@/tpo/Group";

import HeaderNavDropdown from "../common/HeaderNavDropdown";
import HeaderNavLink from "../common/HeaderNavLink";
import { DESKTOP_NAV_DISPLAY, LARGE_DESKTOP_NAV_DISPLAY } from "../common/navDisplay";

function pathMatches(pathname, pattern) {
  return !!matchPath(pathname, { path: pattern, exact: false, strict: false });
}

function linkActive(pathname, pattern) {
  return () => pathMatches(pathname, pattern);
}

export default function CmpDashboardNav() {
  const location = useLocation();
  const organisationContext = useOrganisationContext();
  const { resolvedUrls, isAtUrl } = useCmpRelatedUrl();
  const { practitionerProfile } = useUserProfile();

  const permissions = organisationContext?.permissions ?? [];
  const showReportsTab = practitionerProfile?.showReportsTab;

  const hasPermission = required =>
    !required?.length || required.some(permission => permissions.includes(permission));

  const learningHubLinks = [
    {
      label: "Dashboard",
      to: resolvedUrls[CMP_LEARNING_HUB_DASHBOARD_URL],
      activeTest: () =>
        !!matchPath(location.pathname, {
          path: resolvedUrls[CMP_LEARNING_HUB_DASHBOARD_URL],
          exact: true,
          strict: false
        })
    },
    {
      label: "Webinars",
      to: resolvedUrls[CMP_LEARNING_HUB_WEBINARS_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_LEARNING_HUB_WEBINARS_URL])
    },
    {
      label: "Events",
      to: resolvedUrls[CMP_LEARNING_HUB_EVENTS_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_LEARNING_HUB_EVENTS_URL])
    },
    {
      label: "Courses",
      to: resolvedUrls[CMP_LEARNING_HUB_COURSES_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_LEARNING_HUB_COURSES_URL])
    },
    {
      label: "Videos",
      to: resolvedUrls[CMP_LEARNING_HUB_VIDEO_GUIDES_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_LEARNING_HUB_VIDEO_GUIDES_URL])
    }
  ];

  const shopLinks = [
    {
      label: "Tests",
      to: resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "tests"),
      activeTest: linkActive(
        location.pathname,
        resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "tests")
      )
    },
    ...(showReportsTab
      ? [
          {
            label: "Reports",
            to: resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "reports"),
            activeTest: linkActive(
              location.pathname,
              resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "reports")
            )
          }
        ]
      : []),
    {
      label: "Supplements",
      to: resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "supplements"),
      activeTest: linkActive(
        location.pathname,
        resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "supplements")
      )
    },
    {
      label: "Discounts",
      to: resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "discounts"),
      activeTest: linkActive(
        location.pathname,
        resolvedUrls[CMP_SHOP_TAB_URL].replace(":tab", "discounts")
      )
    }
  ];

  const patientsLinks = [
    {
      label: "Manage patients",
      to: resolvedUrls[CMP_PATIENTS_MANAGE_PATIENTS_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_PATIENTS_MANAGE_PATIENTS_URL])
    },
    {
      label: "Callback requests",
      to: resolvedUrls[CMP_PATIENTS_CALLBACK_REQUESTS_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_PATIENTS_CALLBACK_REQUESTS_URL])
    },
    {
      label: "New results",
      to: resolvedUrls[CMP_PATIENTS_NEW_RESULTS_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_PATIENTS_NEW_RESULTS_URL])
    }
  ];

  const ordersLinks = [
    {
      label: "Direct",
      to: resolvedUrls[CMP_ORDERS_URL],
      activeTest: () =>
        pathMatches(location.pathname, resolvedUrls[CMP_ORDERS_URL]) ||
        pathMatches(location.pathname, resolvedUrls[CMP_ORDER_URL])
    },
    {
      label: "Stock",
      to: resolvedUrls[CMP_STOCK_URL],
      activeTest: linkActive(location.pathname, resolvedUrls[CMP_STOCK_URL])
    }
  ];

  const dashboardActive = isAtUrl(CMP_DASHBOARD_URL, { exact: true })[0];
  const learningHubActive = isAtUrl(CMP_LEARNING_HUB_URL)[0];
  const shopActive =
    isAtUrl(CMP_SHOP_URL)[0] || isAtUrl(CMP_CHECKOUT_URL)[0] || isAtUrl(CMP_SHOP_TAB_URL)[0];
  const patientsActive = isAtUrl(CMP_PATIENTS_URL)[0];
  const ordersActive =
    isAtUrl(CMP_ORDERS_URL)[0] || isAtUrl(CMP_STOCK_URL)[0] || isAtUrl(CMP_ORDER_URL)[0];
  const supportActive = isAtUrl(CMP_SUPPORT_URL)[0];

  return (
    <Group alignItems="center" display={DESKTOP_NAV_DISPLAY} data-component-name="CmpDashboardNav">
      <HeaderNavLink
        label="Dashboard"
        to={resolvedUrls[CMP_DASHBOARD_URL]}
        active={dashboardActive}
        display={DESKTOP_NAV_DISPLAY}
      />
      <HeaderNavDropdown
        label="Learning hub"
        links={learningHubLinks}
        active={learningHubActive}
        display={DESKTOP_NAV_DISPLAY}
      />
      {hasPermission([CMP_PERMISSION_VIEW_SHOP]) && (
        <HeaderNavDropdown
          label="Shop"
          links={shopLinks}
          active={shopActive}
          display={DESKTOP_NAV_DISPLAY}
        />
      )}
      {hasPermission([CMP_PERMISSION_VIEW_RESULTS]) && (
        <HeaderNavDropdown
          label="Patients"
          links={patientsLinks}
          active={patientsActive}
          display={DESKTOP_NAV_DISPLAY}
        />
      )}
      {hasPermission([CMP_PERMISSION_VIEW_ORDERS]) && (
        <HeaderNavDropdown
          label="Orders"
          links={ordersLinks}
          active={ordersActive}
          display={DESKTOP_NAV_DISPLAY}
        />
      )}
      <HeaderNavLink
        label="Support"
        to={resolvedUrls[CMP_SUPPORT_URL]}
        active={supportActive}
        display={LARGE_DESKTOP_NAV_DISPLAY}
      />
    </Group>
  );
}
